<template>
  <div class="lesson-note-box relative">
    <!-- Показуємо placeholder якщо блок пустий -->
    <VLessonPlaceholder v-if="isEmptyBlock" />

    <!-- Показуємо контент якщо блок не пустий -->
    <div v-else
         class="w-full h-full m-0 box-border transition-all relative main-group"
         :style="[
          CssStyleHelpers.margins(fields.mainGroup?.margins),
          CssStyleHelpers.paddings(fields.mainGroup?.paddings),
          {
            '--main-padding-top-mobile': `${fields.mainGroup?.paddingsMobile?.top ?? fields.mainGroup?.paddings?.top ?? 0}px`,
            '--main-padding-right-mobile': `${fields.mainGroup?.paddingsMobile?.right ?? fields.mainGroup?.paddings?.right ?? 0}px`,
            '--main-padding-bottom-mobile': `${fields.mainGroup?.paddingsMobile?.bottom ?? fields.mainGroup?.paddings?.bottom ?? 0}px`,
            '--main-padding-left-mobile': `${fields.mainGroup?.paddingsMobile?.left ?? fields.mainGroup?.paddings?.left ?? 0}px`,
          },
        ]">
      <div v-if="fields.noteGroup?.isShowNoteColor">
        <!-- Контент примечания -->

        <div class="w-full h-full m-0 box-border transition-all relative flex-shrink-0 rounded -[10px]"
             :style="[
              CssStyleHelpers.border(
                {
                  borderLeft: 5,
                  color: fields.noteGroup?.noteColor
                },
                true
              ),
              CssStyleHelpers.background({
                type: 'linear',
                colors: [
                  {color: fields.noteGroup?.noteColor, alpha: 0.3},
                  {color: fields.noteGroup?.noteColor, alpha: 0.07}
                ],
                angle: 90
              },
                true
              ),
            ]">
          <div class="lesson-note-content py-2.5 px-6">
            <TextCard :text="fields.noteGroup?.text" />
          </div>
        </div>
      </div>
      <div v-else
           class="flex">
        <div class=" rounded -[100px] w-[10px] h-fill-available"
             :style="[
              CssStyleHelpers.border(
                {
                  borderLeft: 5,
                  color: fields.noteGroup?.noteColor
                },
                true
              )
            ]"></div>
        <div class="lesson-note-content py-0 px-4">
          <TextCard :text="fields.noteGroup?.text" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed, PropType} from "vue";
import {useDataFields} from "../composables/data";
import VLessonPlaceholder from "../VLessonPlaceholder.vue";
import {ILayoutComponentData} from "../types/ILayoutFieldData";
import {CssStyleHelpers, StyleUtils} from "../utils/cssStyleHelpers";
import TextCard from "../cards/TextCard.vue";

const props = defineProps({
  modelValue: {
    type: Object as PropType<ILayoutComponentData | undefined>,
    required: true,
  },
  mode: {
    type: String as PropType<"view" | "edit">,
    default: "view",
  },
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: ILayoutComponentData): void
}>();

// Використовуємо дані напряму з newData
const applyDefaultMainPaddings = (paddings?: Record<string, number>) => ({
  top: paddings?.top ?? 0,
  right: paddings?.right ?? 0,
  bottom: paddings?.bottom ?? 0,
  left: paddings?.left ?? 0,
  ...paddings,
});

const applyDefaultMainMargins = (margins?: Record<string, number>) => ({
  top: margins?.top ?? 50,
  right: margins?.right ?? 0,
  bottom: margins?.bottom ?? 0,
  left: margins?.left ?? 0,
  ...margins,
});

const fields = computed(() => {
  const newData = props.modelValue?.data;
  if (!newData)
    return {
      noteGroup: {},
      mainGroup: {},
    };

  const {getData} = useDataFields(newData);

  return {
    noteGroup: {
      text: getData("noteGroup.text")?.text,
      noteColor: getData("noteGroup.noteColor")?.value,
      isShowNoteColor: getData("noteGroup.isShowNoteColor")?.value,
    },
    mainGroup: {
      paddings: applyDefaultMainPaddings(getData("mainGroup.paddings")),
      paddingsMobile: applyDefaultMainPaddings(
        getData("mainGroup.paddingsMobile") || getData("mainGroup.paddings")
      ),
      margins: applyDefaultMainMargins(getData("mainGroup.margins")),
    },
  };
});

const isEditMode = computed(() => props.mode === "edit");

// Перевіряємо чи блок пустий
const isEmptyBlock = computed(() => {
  const hasText = fields.value?.noteGroup?.text && fields.value?.noteGroup?.text?.trim() !== '';
  const hasColor = fields.value?.noteGroup?.isShowNoteColor && fields.value?.noteGroup?.noteColor;

  return !(hasText || hasColor);
});


</script>

<style scoped>
@reference "tailwindcss";
/* Основные стили для контейнера */
.lesson-note-box {
  @apply w-full flex flex-col gap-0;
  container-type: inline-size;
}

/* Стили для контента примечания */
.lesson-note-content {
  @apply text-base leading-relaxed relative z-10;
}

/* Адаптивность для мобильных устройств */
@container (max-width: 700px) {
  .main-group {
    padding-top: var(--main-padding-top-mobile) !important;
    padding-right: var(--main-padding-right-mobile) !important;
    padding-bottom: var(--main-padding-bottom-mobile) !important;
    padding-left: var(--main-padding-left-mobile) !important;
  }

  .lesson-note-content {
    @apply text-sm;
  }
}
</style>
<style>
@import "../styles/dialogs-styles.css";
</style>
